import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import JobManager from "../components/JobManager";
import ContestacaoInputDisplay from "../components/ContestacaoInputDisplay";
import ContestacaoOutputDisplay from "../components/ContestacaoOutputDisplay";
import CellParamsModal from "../components/CellParamsModal";
import {
  Section,
  MetaGrid,
  MetaRow,
  JobMetaSection,
} from "../components/JobDetailsDrawer";
import {
  ContestacaoConfigSection,
  ContestacaoTesesSection,
  ContestacaoRulesSection,
  ContestacaoActionsSection,
} from "../components/ContestacaoDrawerSections";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/ApiKeyContext";
import {
  Settings,
  FindInPage,
  Archive,
  Unarchive,
  Sync,
  DeleteOutline,
  FileCopy,
  RecordVoiceOver,
  Description,
  AssignmentTurnedIn,
  PlaylistAddCheck,
  MenuBook,
} from "@material-ui/icons";

import { getApiBaseUrl } from "../config";
import SoftDeleteJobDialog from "../components/SoftDeleteJobDialog";
import { jobAllowsManualReprocess } from "../utils/jobReprocess";
import { DOCUMENT_CREATION_PRODUCT_CODES } from "../utils/serviceCustomizationApi";
import {
  persistContestacaoJobForReplication,
  contestacaoJobAllowsRecreateFromRow,
} from "../utils/replicateContestacaoJob";
import {
  getJobStatusLabel,
  isAwaitingContestationReview,
  isAwaitingTranscriptionReview,
} from "../utils/jobTaskStatus";
import { getEmbedListingQuery, isEmbedMode } from "../constants/embedMode";

const API_BASE_URL = getApiBaseUrl();

/** Status em que a tela de revisão faz sentido (pendente ou já finalizado — só leitura). */
function canOpenContestacaoReviewScreen(job) {
  if (isAwaitingContestationReview(job)) return true;
  const s = job?.status;
  return s === "DONE" || s === "APPROVED" || s === "FINALIZING";
}

const SOFT_DELETE_ALLOWED_STATUSES = ["DONE", "FINAL_ERROR", "ERROR_FINALIZING"];

const ContestacaoPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation("jobs");
  const { cell, hasRole, user, token } = useAuth();
  const embedMode = isEmbedMode();
  const embedListingQuery = embedMode ? getEmbedListingQuery() : {};

  // Estado para controlar o modal de parâmetros da célula
  const [cellParamsModalOpen, setCellParamsModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [initialJobId, setInitialJobId] = useState(null);
  const [softDeleteTarget, setSoftDeleteTarget] = useState(null);

  // Detectar jobId na query string e abrir automaticamente
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const jobId = params.get("jobId");
    if (jobId) {
      setInitialJobId(parseInt(jobId, 10));
      // Limpar o query param da URL para não reabrir o job ao recarregar
      history.replace("/contestacoes");
    }
  }, [location.search, history]);

  /* A tabela mostra apenas o essencial para identificação rápida do job;
     todo o restante (célula, total de páginas, arquivos de entrada, etc.)
     foi movido para o drawer lateral acionado pelo botão ">". */
  const columns = [
    {
      key: "id",
      label: t("contestacao.columns.id"),
      type: "id",
      sortable: false,
    },
    {
      key: "insertion_timestamp",
      label: t("contestacao.columns.dateTime"),
      type: "date",
      sortable: true,
    },
    {
      key: "job_name",
      label: t("table.taskName"),
      type: "text",
      sortable: true,
    },
    {
      key: "status",
      label: t("contestacao.columns.status"),
      type: "status",
      sortable: true,
      align: "center",
      formatValue: (value, job) => getJobStatusLabel(value, job, t),
    },
    {
      key: "user_name",
      label: t("contestacao.columns.user"),
      type: "user",
      sortable: true,
    },
    {
      key: "output_payload",
      label: t("contestacao.columns.generatedDocument"),
      type: "custom",
      sortable: false,
      align: "center",
      renderCell: (job) => (
        <ContestacaoOutputDisplay
          outputPayload={job.output_payload}
          status={job.status}
          job={job}
        />
      ),
    },
    {
      key: "actions",
      label: t("table.actions"),
      type: "actions",
      sortable: false,
    },
  ];

  // Configuração dos campos de filtro específicos para contestação
  const filterFields = [
    // Filtros obrigatórios para todos os tipos de trabalho
    {
      key: "job_id",
      label: t("filters.jobId"),
      type: "text",
      placeholder: t("filters.jobIdPlaceholder"),
    },
    {
      key: "job_name_filter",
      label: t("filters.taskName"),
      type: "text",
      placeholder: t("filters.taskNamePlaceholder"),
    },
    {
      key: "external_id",
      label: t("filters.externalId"),
      type: "text",
      placeholder: t("filters.externalIdPlaceholder"),
    },
    {
      key: "date_start",
      label: t("filters.dateStart"),
      type: "date",
    },
    {
      key: "date_end",
      label: t("filters.dateEnd"),
      type: "date",
    },
    // Filtros específicos para contestação
    {
      key: "user_name_filter",
      label: t("filters.user"),
      type: "text",
      placeholder: t("filters.userPlaceholder"),
    },
    {
      key: "status_filter",
      label: t("filters.status"),
      type: "select",
      options: [
        { value: "WAITING", label: t("status.WAITING") },
        { value: "RUNNING", label: t("status.RUNNING") },
        { value: "DONE", label: t("status.DONE") },
        { value: "ERROR", label: t("status.ERROR") },
        { value: "FINAL_ERROR", label: t("status.FINAL_ERROR") },
        { value: "DISCARDED", label: t("status.DISCARDED") },
      ],
    },
    {
      key: "task_type_filter",
      label: t("filters.taskType"),
      type: "select",
      options: [
        {
          value: "DOSSIER_CREATION",
          label: t("jobStatusViewer.taskTypes.DOSSIER_CREATION"),
        },
        {
          value: "DOSSIER_ANALYSIS",
          label: t("jobStatusViewer.taskTypes.DOSSIER_ANALYSIS"),
        },
        {
          value: "CONTESTATION_CREATION",
          label: t("jobStatusViewer.taskTypes.CONTESTATION_CREATION"),
        },
        {
          value: "CONTESTATION_REVIEW",
          label: t("jobStatusViewer.taskTypes.CONTESTATION_REVIEW"),
        },
        {
          value: "FINAL_DOC_CREATION",
          label: t("jobStatusViewer.taskTypes.FINAL_DOC_CREATION"),
        },
      ],
    },
    {
      key: "output_payload_text_filter",
      label: t("contestacao.filters.documentContent"),
      type: "text",
      placeholder: t("contestacao.filters.documentContentPlaceholder"),
    },
    {
      key: "include_archived",
      label: t("filters.showArchived"),
      type: "select",
      options: [
        { value: "", label: t("filters.showArchivedNo") },
        { value: "true", label: t("filters.showArchivedYes") },
      ],
    },
  ];

  const hasReviewerPermission = () => {
    return user && hasRole("DOCUMENT_CREATION", "reviewer");
  };

  const hasContestacaoListAccess = () =>
    user &&
    (hasRole("DOCUMENT_CREATION", "view_jobs") ||
      hasRole("DOCUMENT_CREATION", "exec_process") ||
      hasRole("DOCUMENT_CREATION", "reviewer"));

  /** Revisor: pendente de revisão. Demais usuários da lista: só após conclusão (visualização). */
  const shouldShowContestacaoReviewButton = (job) => {
    if (!canOpenContestacaoReviewScreen(job)) {
      return false;
    }
    if (isAwaitingContestationReview(job)) {
      return hasReviewerPermission();
    }
    return hasContestacaoListAccess();
  };

  const apiHeaders = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ServiceName: "DOCUMENT_CREATION",
  };

  const handleArchiveJob = async (job) => {
    try {
      const response = await fetch(`${API_BASE_URL}/job/${job.id}/archive`, {
        method: "PATCH",
        headers: apiHeaders,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao arquivar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao arquivar tarefa:", error);
    }
  };

  const handleUnarchiveJob = async (job) => {
    try {
      const response = await fetch(`${API_BASE_URL}/job/${job.id}/unarchive`, {
        method: "PATCH",
        headers: apiHeaders,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao desarquivar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao desarquivar tarefa:", error);
    }
  };

  const handleSoftDeleteJob = async (job) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/job/${job.id}/soft_delete`,
        {
          method: "PATCH",
          headers: apiHeaders,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao ocultar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao ocultar tarefa:", error);
    }
  };

  const handleBatchArchive = async (jobIds) => {
    if (!jobIds.length) return;
    try {
      const response = await fetch(`${API_BASE_URL}/job/batch_archive`, {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify({ job_ids: jobIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao arquivar em lote");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao arquivar em lote:", error);
    }
  };

  const handleBatchUnarchive = async (jobIds) => {
    if (!jobIds.length) return;
    try {
      const response = await fetch(`${API_BASE_URL}/job/batch_unarchive`, {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify({ job_ids: jobIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao desarquivar em lote");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao desarquivar em lote:", error);
    }
  };

  const handleReprocessJob = async (job) => {
    try {
      const response = await fetch(`${API_BASE_URL}/job/${job.id}/reprocess`, {
        method: "PATCH",
        headers: apiHeaders,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao reprocessar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao reprocessar tarefa:", error);
    }
  };

  const canReprocess = hasRole("ADMIN", "re_execute_job");
  const canArchive = hasRole("DOCUMENT_CREATION", "archive_job");
  const canDelete = hasRole("DOCUMENT_CREATION", "delete_job");
  const canRecreateFromTask = hasRole("DOCUMENT_CREATION", "exec_process");

  /* Define o rótulo do botão primário "review" conforme a etapa do fluxo:
       - task=CONTESTATION_REVIEW + status RUNNING/PENDING_APPROVAL  → ação
         humana de revisão da contestação;
       - task=CONTESTATION_CREATION + status DONE  → as referências da
         contestação já podem ser visualizadas;
       - task=FINAL_DOC_CREATION (qualquer status) → idem, mantém o mesmo
         destino para que o revisor (ou demais usuários da lista) acesse a
         tela e veja as referências do documento final.
     Para os demais casos cobertos por `shouldShowContestacaoReviewButton`
     (DONE/APPROVED/FINALIZING genérico) caímos no rótulo padrão "Revisar". */
  /* Diferencia o botão "Visualizar Referências" (consulta de processamento já
     concluído) do "Revisão da contestação" (ação humana pendente).
     Quando é só consulta o botão usa o estilo `ghost` (outlined), bem mais
     discreto, já que não há urgência. */
  const isReviewActionForViewing = (job) => {
    if (isAwaitingContestationReview(job)) return false;
    const task = job?.task;
    const status = job?.status;
    return (
      (task === "CONTESTATION_CREATION" && status === "DONE") ||
      task === "FINAL_DOC_CREATION"
    );
  };

  const buildReviewLabel = (job) => {
    if (isAwaitingContestationReview(job)) {
      return t("actions.primary.reviewContestation");
    }
    if (isReviewActionForViewing(job)) {
      return t("actions.primary.viewReferences");
    }
    return t("actions.primary.review");
  };

  const buildReviewVariant = (job) =>
    isReviewActionForViewing(job) ? "ghost" : "contained";

  /* Ações customizadas — a hierarquia visual é definida pelo `tier`:
       - "primary"  : botão preenchido. apenas a primeira primária visível ganha
                      destaque (revisão > revisão de transcrição > tentar novamente).
       - "secondary": IconButton inline, cinza neutro.
       - "menu"     : entra no menu "⋮" (ações de gerenciamento).
       - "danger"   : também no menu, mas em vermelho (ações destrutivas). */
  const customActions = [
    {
      key: "review",
      tier: "primary",
      label: buildReviewLabel,
      variant: buildReviewVariant,
      icon: <FindInPage />,
      tooltip: t("contestacao.actions.review"),
      hidden: (job) => !shouldShowContestacaoReviewButton(job),
    },
    {
      key: "transcription_review",
      tier: "primary",
      label: t("actions.primary.transcriptionReview"),
      icon: <RecordVoiceOver />,
      tooltip: t("actions.transcriptionReviewTooltip"),
      hidden: (job) => !isAwaitingTranscriptionReview(job),
    },
    ...(canReprocess
      ? [
          {
            key: "reprocess",
            tier: "primary",
            label: t("actions.primary.retry"),
            icon: <Sync />,
            tooltip: t("actions.reprocessTooltip"),
            hidden: (job) => !jobAllowsManualReprocess(job),
            onClick: (job) => handleReprocessJob(job),
          },
        ]
      : []),
    {
      key: "recreate_from_task",
      tier: "menu",
      icon: <FileCopy />,
      label: t("actions.recreateFromTask"),
      tooltip: t("actions.recreateFromTaskTooltip"),
      hidden: (job) =>
        !canRecreateFromTask || !contestacaoJobAllowsRecreateFromRow(job),
      onClick: (job) => {
        if (!persistContestacaoJobForReplication(job)) {
          return;
        }
        history.push("/contestacao/nova");
      },
    },
    {
      key: "archive",
      tier: "menu",
      icon: <Archive />,
      label: t("actions.archive"),
      tooltip: t("actions.archiveTooltip"),
      hidden: (job) =>
        !canArchive ||
        !!job.archived_timestamp ||
        !["DONE", "FINAL_ERROR", "ERROR_FINALIZING"].includes(job.status),
      onClick: (job) => handleArchiveJob(job),
    },
    {
      key: "unarchive",
      tier: "menu",
      icon: <Unarchive />,
      label: t("actions.unarchive"),
      tooltip: t("actions.unarchiveTooltip"),
      hidden: (job) => !canArchive || !job.archived_timestamp,
      onClick: (job) => handleUnarchiveJob(job),
    },
    {
      key: "soft_delete",
      tier: "danger",
      icon: <DeleteOutline />,
      label: t("actions.softDelete"),
      tooltip: t("actions.softDeleteTooltip"),
      hidden: (job) =>
        !canDelete ||
        !SOFT_DELETE_ALLOWED_STATUSES.includes(job.status),
      onClick: (job) => setSoftDeleteTarget(job),
    },
  ];

  // Handlers para ações
  const handleNewJob = () => {
    history.push("/contestacao/nova");
  };

  const handleJobClick = (job) => {
    console.log("Contestação clicada:", job);
    // Navegar para detalhes da contestação
    history.push(`/contestacao/detalhes/${job.id}`);
  };

  const handleJobAction = (action, job) => {
    console.log("Ação executada:", action, "na contestação:", job);

    switch (action) {
      case "view":
        // Visualizar detalhes da contestação
        console.log("Visualizar contestação:", job.id);
        history.push(`/contestacao/detalhes/${job.id}`);
        break;

      case "download":
        // Fazer download do documento gerado
        console.log("Download documento:", job.id);
        downloadContestacaoDocument(job);
        break;

      case "review":
        history.push(`/documento/revisar/contestacao/${job.id}`);
        break;

      case "transcription_review":
        history.push(`/transcricao/revisar/${job.id}`);
        break;

      case "archive":
        handleArchiveJob(job);
        break;

      case "unarchive":
        handleUnarchiveJob(job);
        break;

      case "soft_delete":
        setSoftDeleteTarget(job);
        break;

      case "reprocess":
        handleReprocessJob(job);
        break;

      case "regenerate":
        // Regenerar o documento
        console.log("Regenerar documento:", job.id);
        regenerateDocument(job);
        break;

      default:
        console.log("Ação desconhecida:", action);
    }
  };

  const downloadContestacaoDocument = (job) => {
    // Implementar download do documento da contestação
    if (job.output_payload) {
      try {
        // Assumindo que o output_payload contém um documento em formato específico
        const blob = new Blob([job.output_payload], {
          type: "application/pdf",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `contestacao_${job.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Erro ao fazer download:", error);
      }
    }
  };

  const regenerateDocument = (job) => {
    // Implementar regeneração do documento
    console.log("Regenerando documento para contestação:", job.id);
    // Aqui você faria uma chamada para a API para regenerar o documento
  };

  // Função para verificar se o usuário tem permissão para configurar parâmetros da célula
  const hasContestParamsPermission = () => {
    const pc = DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO;
    return (
      hasRole("DOCUMENT_CREATION", "edit_custom_default", pc) ||
      hasRole("DOCUMENT_CREATION", "edit_custom_super_user", pc)
    );
  };

  // Função para abrir o modal de configuração de parâmetros
  const handleOpenCellParams = () => {
    setCellParamsModalOpen(true);
  };

  // Função para fechar o modal de configuração de parâmetros
  const handleCloseCellParams = () => {
    setCellParamsModalOpen(false);
  };

  // Criar ações customizadas do header específicas para contestação
  const customHeaderActions = hasContestParamsPermission()
    ? [
        {
          icon: <Settings />,
          onClick: handleOpenCellParams,
          disabled: !cell?.value, // Desabilita se não há célula selecionada
          style: {
            color: "var(--ikaro-orange-primary)",
          },
        },
      ]
    : [];

  /* Conteúdo do drawer lateral de detalhes da contestação. A ordem segue a
     prioridade de uso por advogados em operação: identificação (geral) →
     etapa do fluxo → configuração da geração (modo + parâmetros) → teses
     utilizadas → regras aplicadas → arquivos de entrada → documento gerado →
     ações disponíveis no rodapé (respeitando permissões do usuário). */
  const renderContestacaoDetails = (job) => {
    const totalPages = job?.input_payload?.total_paginas;
    const taskLabel = job?.task
      ? t(`jobStatusViewer.taskTypes.${job.task}`, job.task)
      : null;
    const statusLabel = getJobStatusLabel(job?.status, job, t);
    return (
      <>
        <JobMetaSection job={job} />

        <Section
          title={t("contestacao.drawer.sections.task")}
          accent="neutral"
        >
          <MetaGrid>
            <MetaRow
              icon={<AssignmentTurnedIn />}
              label={t("contestacao.drawer.fields.taskName")}
              value={job?.job_name || null}
              copyValue={job?.job_name || null}
            />
            <MetaRow
              icon={<PlaylistAddCheck />}
              label={t("contestacao.drawer.fields.task")}
              value={taskLabel}
              copyValue={null}
            />
            <MetaRow
              icon={<MenuBook />}
              label={t("contestacao.drawer.fields.statusDetail")}
              value={statusLabel}
              copyValue={null}
            />
            <MetaRow
              icon={<Description />}
              label={t("contestacao.drawer.fields.totalPages")}
              value={totalPages != null ? String(totalPages) : null}
              copyValue={null}
              emptyText={t("contestacao.drawer.fields.totalPagesEmpty")}
            />
          </MetaGrid>
        </Section>

        <ContestacaoConfigSection job={job} />

        <ContestacaoTesesSection job={job} />

        <ContestacaoRulesSection job={job} />

        <Section
          title={t("contestacao.drawer.sections.input")}
          accent="primary"
        >
          <ContestacaoInputDisplay
            inputPayload={job?.input_payload}
            forceExpanded
          />
        </Section>

        <Section
          title={t("contestacao.drawer.sections.output")}
          accent="secondary"
        >
          <ContestacaoOutputDisplay
            outputPayload={job?.output_payload}
            status={job?.status}
            job={job}
          />
        </Section>

        <ContestacaoActionsSection
          job={job}
          actions={customActions}
          onAction={handleJobAction}
        />
      </>
    );
  };

  return (
    <>
      <JobManager
        jobType="contestacao"
        title={t("contestacao.title")}
        subtitle={t("contestacao.subtitle")}
        apiEndpoint="/job/get_jobs_list"
        createJobPath="/contestacao/nova"
        columns={columns}
        filterFields={filterFields}
        customActions={customActions}
        customHeaderActions={customHeaderActions}
        onNewJob={handleNewJob}
        onJobClick={handleJobClick}
        onJobAction={handleJobAction}
        code="DC_CONTESTACAO"
        serviceName="DOCUMENT_CREATION"
        newJobButtonLabel={t("contestacao.newContestation")}
        filterButtonLabel={t("header.filters")}
        customFilters={embedListingQuery}
        showNewJobButton={
          !embedMode && hasRole("DOCUMENT_CREATION", "exec_process")
        }
        hideDefaultActions={true}
        refreshTrigger={refreshTrigger}
        enableSelection={true}
        onBatchArchive={handleBatchArchive}
        onBatchUnarchive={handleBatchUnarchive}
        initialJobId={initialJobId}
        renderJobDetails={renderContestacaoDetails}
        drawerTitle={t("contestacao.drawer.title")}
      />

      {/* Modal de configuração de parâmetros da célula */}
      <CellParamsModal
        open={cellParamsModalOpen}
        onClose={handleCloseCellParams}
        cellId={cell?.value}
      />

      <SoftDeleteJobDialog
        open={softDeleteTarget != null}
        job={softDeleteTarget}
        onClose={() => setSoftDeleteTarget(null)}
        onConfirm={handleSoftDeleteJob}
      />
    </>
  );
};

export default ContestacaoPage;
